import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import config from '../../../config.json'
import { Getter, Setter } from '../../../services/ComService'
import {
  PrintFormats,
  expectedPaperSizeMm,
  findPrintFormat,
  workbookPdfPrinterName,
} from '../../DTO/PrintFormats'
import { printPdfWithQz, qzErrorMessage } from '../../services/QzPrint'
import { MItem } from '../Menu'
import PlaningToolRoot from '../PlaningToolRoot'

type VerifyResult = {
  ok: boolean,
  match?: boolean,
  orientationMatch?: 'portrait' | 'landscape' | null,
  error?: string,
  expected?: { widthMm: number, heightMm: number, toleranceMm: number },
  actual?: { widthMm: number, heightMm: number, widthPts: number, heightPts: number, pages: number | null },
  file?: string,
}

export default function PrintSandbox() {
  const [formatValue, setFormatValue] = useState('moderationA6')
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape')
  const [pdfUrl, setPdfUrl] = useState(`${window.location.origin}/print-sandbox/moka1.pdf`)
  const [statusMsg, setStatusMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const [apiOk, setApiOk] = useState<boolean | null>(null)
  const [verify, setVerify] = useState<VerifyResult | null>(null)
  const format = findPrintFormat(formatValue)
  const printer = workbookPdfPrinterName(formatValue)
  const expected = expectedPaperSizeMm(formatValue)

  useEffect(() => {
    Getter('print-sandbox/status').then((data: { ok?: boolean }) => {
      setApiOk(!!data?.ok)
    }).catch(() => setApiOk(false))
  }, [])

  const run = async () => {
    if (!format || !expected) {
      setStatusMsg('Unbekanntes Format')
      return
    }
    setBusy(true)
    setVerify(null)
    setStatusMsg('Drucke über QZ…')
    const since = Math.floor(Date.now() / 1000) - 2
    try {
      await printPdfWithQz({
        url: pdfUrl,
        formatValue: format.value,
        formatTitle: format.title,
        widthMm: format.widthMm,
        heightMm: format.heightMm,
        orientation,
        sides: 'simplex',
        color: 'color',
        quality: '',
      }, { printer })
      setStatusMsg('Job gesendet — prüfe Ausgabe…')
      // cups-pdf braucht oft ein paar Sekunden
      let result: VerifyResult | null = null
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 1000))
        try {
          result = await Setter('print-sandbox/verify', {
            widthMm: expected.widthMm,
            heightMm: expected.heightMm,
            since,
          }) as VerifyResult
          if (result?.file) { break }
        } catch (err: any) {
          // 404 = noch kein File
          if (err?.status && err.status !== 404 && err.status !== 'fail') {
            throw err
          }
        }
      }
      if (!result?.file) {
        setVerify({ ok: false, error: 'Kein PDF innerhalb von 15s gefunden. Liegt die Ausgabe in ~/PDF?' })
        setStatusMsg('Keine Ausgabe gefunden')
      } else {
        setVerify(result)
        setStatusMsg(result.match ? 'Format stimmt' : 'Format weicht ab')
      }
    } catch (error) {
      setStatusMsg(qzErrorMessage(error))
      setVerify({ ok: false, error: qzErrorMessage(error) })
    } finally {
      setBusy(false)
    }
  }

  return <PlaningToolRoot standalone menuItem={MItem.preferences} mainContentClass='PrintSandbox'>
    <Box className='w3-container' sx={{ maxWidth: 720, py: 2 }}>
      <Typography variant='h4' gutterBottom>Druck-Sandbox</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        QZ → lokale Queue <code>{printer}</code> → PDF in ~/PDF → Auto-Check der Seitengröße.
        Queues: <code>tools/workbook-pdf-printer/install.sh</code>
      </Typography>

      {apiOk === false &&
        <Alert severity='warning' sx={{ mb: 2 }}>
          Verify-API nicht erreichbar (nur in API-dev). Status: {config.apiPrefix}print-sandbox/status
        </Alert>
      }
      {apiOk &&
        <Alert severity='success' sx={{ mb: 2 }}>Verify-API bereit</Alert>
      }

      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel>Format</InputLabel>
          <Select
            label='Format'
            value={formatValue}
            onChange={(e) => setFormatValue(e.target.value)}
          >
            {PrintFormats.map(f => (
              <MenuItem key={f.value} value={f.value}>
                {f.title} ({f.widthMm}×{f.heightMm} mm)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Orientierung</InputLabel>
          <Select
            label='Orientierung'
            value={orientation}
            onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
          >
            <MenuItem value='portrait'>hoch (portrait)</MenuItem>
            <MenuItem value='landscape'>quer (landscape)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label='PDF-URL (http/https)'
          value={pdfUrl}
          onChange={(e) => setPdfUrl(e.target.value)}
          fullWidth
          helperText='Demo: /print-sandbox/moka1.pdf — QZ akzeptiert kein file://'
        />

        {expected &&
          <Alert severity='info'>
            Erwartetes Blatt: {expected.widthMm}×{expected.heightMm} mm
            {format?.defaultMedia ? ` (media ${format.defaultMedia})` : ''}
            {' · '}Drucker: {printer}
          </Alert>
        }

        <Button variant='contained' disabled={busy || !pdfUrl} onClick={run}>
          {busy ? 'Läuft…' : 'Drucken & prüfen'}
        </Button>

        {statusMsg &&
          <Typography variant='body2'>{statusMsg}</Typography>
        }

        {verify &&
          <Alert severity={verify.match ? 'success' : 'error'}>
            {verify.match
              ? `OK — ${verify.actual?.widthMm}×${verify.actual?.heightMm} mm`
              + (verify.orientationMatch ? ` (${verify.orientationMatch})` : '')
              : (verify.error || `Abweichung — Ist ${verify.actual?.widthMm}×${verify.actual?.heightMm} mm, Soll ${verify.expected?.widthMm}×${verify.expected?.heightMm} mm`)
            }
            {verify.file &&
              <Box component='div' sx={{ mt: 1, wordBreak: 'break-all', fontSize: '0.85em' }}>
                {verify.file}
              </Box>
            }
          </Alert>
        }
      </Stack>
    </Box>
  </PlaningToolRoot>
}
